import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DevicePreferences } from '../../types/questionData';
import { generateDeviceId, getDeviceInfo } from '../../utils/deviceInfo';

export class DevicePreferencesService {
  private static instance: DevicePreferencesService;
  private deviceId: string;
  private tenantId: number | null = null;
  private userId: number | string | null = null;
  private cachedPreferences: DevicePreferences | null = null;
  private isOnline: boolean = navigator.onLine;
  private pendingUpdates: Partial<DevicePreferences['preferences']> | null = null;

  private constructor() {
    this.deviceId = generateDeviceId();
    
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('[DevicePreferences] Connection restored, syncing pending updates...');
      this.syncPendingUpdates();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('[DevicePreferences] Offline mode - changes will be saved locally');
    });
  }

  static getInstance(): DevicePreferencesService {
    if (!this.instance) {
      this.instance = new DevicePreferencesService();
    }
    return this.instance;
  }

  setUserContext(tenantId: number, userId: number | string) {
    this.tenantId = tenantId;
    this.userId = userId;
    // Clear cache when user context changes
    this.cachedPreferences = null;
  }

  private getCollectionPath(): string {
    if (!this.tenantId || !this.userId) {
      throw new Error('User context not set');
    }
    return `${this.tenantId}_${this.userId}_${this.deviceId}`;
  }

  async loadPreferences(): Promise<DevicePreferences | null> {
    try {
      // Return cached preferences if available
      if (this.cachedPreferences) {
        return this.cachedPreferences;
      }

      // Try to load from localStorage first
      const localPreferences = this.loadFromLocalStorage();
      
      if (!this.isOnline) {
        // Offline - use localStorage or defaults
        const preferences = localPreferences || this.createDefaultPreferences();
        this.cachedPreferences = preferences;
        return preferences;
      }

      // Online - try Firebase
      try {
        const docRef = doc(db, 'devicePreferences', this.getCollectionPath());
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const firebasePreferences = docSnap.data() as DevicePreferences;
          this.cachedPreferences = firebasePreferences;
          // Update localStorage with latest from Firebase
          this.saveToLocalStorage(firebasePreferences);
          return firebasePreferences;
        }
      } catch (firebaseError) {
        console.log('[DevicePreferences] Using local cache');
        // Fall back to localStorage if Firebase fails
        if (localPreferences) {
          this.cachedPreferences = localPreferences;
          return localPreferences;
        }
      }

      // Create default preferences for new device
      const defaultPreferences = this.createDefaultPreferences();
      // Try to save to Firebase, but don't fail if it doesn't work
      this.savePreferences(defaultPreferences).catch(() => {
        // Silently handle initial save failure
      });
      return defaultPreferences;
    } catch (error) {
      console.error('Error loading device preferences:', error);
      // Return defaults as last resort
      const defaults = this.createDefaultPreferences();
      this.cachedPreferences = defaults;
      return defaults;
    }
  }

  async savePreferences(preferences: Partial<DevicePreferences>): Promise<boolean> {
    try {
      if (!this.tenantId || !this.userId) {
        console.error('User context not set');
        return false;
      }

      const deviceInfo = getDeviceInfo();
      const fullPreferences: DevicePreferences = {
        tenantId: this.tenantId,
        userId: this.userId,
        deviceId: this.deviceId,
        deviceInfo,
        preferences: preferences.preferences || this.createDefaultPreferences().preferences,
        createdAt: this.cachedPreferences?.createdAt || Date.now(),
        updatedAt: Date.now()
      };

      // Always update cache and localStorage
      this.cachedPreferences = fullPreferences;
      this.saveToLocalStorage(fullPreferences);

      // Try to save to Firebase if online
      if (this.isOnline) {
        try {
          const docRef = doc(db, 'devicePreferences', this.getCollectionPath());
          await setDoc(docRef, fullPreferences);
          return true;
        } catch (firebaseError) {
          console.log('[DevicePreferences] Firebase temporarily unavailable, saved locally');
          // Store updates for later sync
          this.pendingUpdates = fullPreferences.preferences;
          return true; // Still return true since we saved locally
        }
      } else {
        // Offline - store for later sync
        this.pendingUpdates = fullPreferences.preferences;
        console.log('[DevicePreferences] Saved locally (offline mode)');
        return true;
      }
    } catch (error) {
      console.error('Error saving device preferences:', error);
      return false;
    }
  }

  async updatePreferences(updates: Partial<DevicePreferences['preferences']>): Promise<boolean> {
    try {
      // Use cached preferences or load them
      const current = this.cachedPreferences || await this.loadPreferences();
      if (!current) return false;

      // Filter out undefined values
      const cleanedUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const updatedPreferences = {
        ...current,
        preferences: {
          ...current.preferences,
          ...cleanedUpdates,
          penSettings: cleanedUpdates.penSettings ? {
            ...current.preferences.penSettings,
            ...cleanedUpdates.penSettings
          } : current.preferences.penSettings
        },
        updatedAt: Date.now()
      };

      return this.savePreferences(updatedPreferences);
    } catch (error) {
      console.error('Error updating device preferences:', error);
      // Try to save locally at least
      if (this.cachedPreferences) {
        this.pendingUpdates = { ...this.pendingUpdates, ...updates };
        return true;
      }
      return false;
    }
  }

  private createDefaultPreferences(): DevicePreferences {
    const deviceInfo = getDeviceInfo();
    
    return {
      tenantId: this.tenantId!,
      userId: this.userId!,
      deviceId: this.deviceId,
      deviceInfo,
      preferences: {
        penMode: false,
        touchEnabled: true,
        penSettings: {
          sensitivity: 1,
          smoothing: 0.5,
          pressureSensitivity: true,
          tiltSupport: false
        },
        toolbarCompact: false,
        defaultTool: 'pen',
        defaultColor: '#000000',
        defaultWidth: 4,
        glassEffects: false
      },
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  private getLocalStorageKey(): string {
    return `devicePreferences_${this.tenantId}_${this.userId}_${this.deviceId}`;
  }

  private saveToLocalStorage(preferences: DevicePreferences): void {
    try {
      localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  private loadFromLocalStorage(): DevicePreferences | null {
    try {
      const stored = localStorage.getItem(this.getLocalStorageKey());
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  }

  private async syncPendingUpdates(): Promise<void> {
    if (!this.pendingUpdates || !this.isOnline) return;

    try {
      const current = this.cachedPreferences || this.loadFromLocalStorage() || this.createDefaultPreferences();
      const updatedPreferences = {
        ...current,
        preferences: {
          ...current.preferences,
          ...this.pendingUpdates
        },
        updatedAt: Date.now()
      };

      const docRef = doc(db, 'devicePreferences', this.getCollectionPath());
      await setDoc(docRef, updatedPreferences);
      
      this.pendingUpdates = null;
      console.log('[DevicePreferences] Synced successfully');
    } catch (error) {
      console.error('Error syncing pending updates:', error);
    }
  }
}

export const devicePreferencesService = DevicePreferencesService.getInstance();